import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import HuntCanvas from './HuntCanvas';
import HuntHUD from './HuntHUD';
import HuntLeaderboard from './HuntLeaderboard';
import Button from '../Button';
import { UserContext } from '../../contexts/UserContext';
import { useAuthenticatedAction } from '../../hooks/useAuthenticatedAction';
import { getLeaderboard, submitScore } from '../../services/hunt';
import { HuntLeaderboardResponse, HuntSubmitScoreResponse } from '../../services/types';
import {
    BIRD_POINTS,
    getLocalBest,
    HUNT_ONLINE_FEATURES,
    updateLocalBest,
} from './huntConfig';
import { buildGameResult, HuntGameSnapshot } from './huntEngine';
import './HuntGame.css';

type GamePhase = 'idle' | 'playing' | 'gameover';

const HuntGame: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const { execute, loading: submitting } = useAuthenticatedAction<HuntSubmitScoreResponse>();

    const [sessionKey, setSessionKey] = useState(0);
    const [phase, setPhase] = useState<GamePhase>('idle');
    const [snapshot, setSnapshot] = useState<HuntGameSnapshot | null>(null);
    const [finalSnapshot, setFinalSnapshot] = useState<HuntGameSnapshot | null>(null);
    const [localBest, setLocalBest] = useState(getLocalBest());
    const [leaderboardDay, setLeaderboardDay] = useState<HuntLeaderboardResponse | null>(null);
    const [leaderboardWeek, setLeaderboardWeek] = useState<HuntLeaderboardResponse | null>(null);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [beatPersonalBest, setBeatPersonalBest] = useState(false);

    const reducedMotion = useMemo(
        () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        []
    );

    const loadLeaderboards = useCallback(async () => {
        if (!HUNT_ONLINE_FEATURES) {
            return;
        }
        setLeaderboardLoading(true);
        try {
            const [day, week] = await Promise.all([getLeaderboard('day'), getLeaderboard('week')]);
            setLeaderboardDay(day);
            setLeaderboardWeek(week);
        } catch (err) {
            console.error('Failed to load hunt leaderboards', err);
        } finally {
            setLeaderboardLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLeaderboards();
    }, [loadLeaderboards]);

    const handleSnapshot = useCallback((snap: HuntGameSnapshot) => {
        setSnapshot(snap);
    }, []);

    const handleFinished = useCallback(async (finishedSnapshot: HuntGameSnapshot) => {
        setPhase('gameover');
        setFinalSnapshot(finishedSnapshot);

        const result = buildGameResult(finishedSnapshot);
        const previousBest = getLocalBest();
        const updatedBest = updateLocalBest(result.score);
        setLocalBest(updatedBest);
        setBeatPersonalBest(result.score > previousBest);

        if (HUNT_ONLINE_FEATURES && currentUser) {
            const submitted = await execute(submitScore, {
                score: result.score,
                duration_ms: result.durationMs,
                mode: result.mode,
                hits: result.hits,
                combo_bonus: result.comboBonus,
            });
            if (submitted) {
                await loadLeaderboards();
            }
        }
    }, [currentUser, execute, loadLeaderboards]);

    const startGame = () => {
        setSessionKey((k) => k + 1);
        setPhase('playing');
        setFinalSnapshot(null);
        setBeatPersonalBest(false);
        setSnapshot(null);
    };

    const champion = HUNT_ONLINE_FEATURES ? leaderboardDay?.champion ?? null : null;

    const renderHitStats = (snap: HuntGameSnapshot) => {
        const parts = [
            `${snap.hits.normal} oiseau${snap.hits.normal > 1 ? 'x' : ''}`,
            `${snap.hits.fast} rapide${snap.hits.fast > 1 ? 's' : ''}`,
            `${snap.hits.bonus} corbac${snap.hits.bonus > 1 ? 's' : ''}`,
            `+${snap.comboBonus} combo`,
        ];
        return parts.join(' · ');
    };

    return (
        <div className="HuntGame">
            {champion && (
                <div className="HuntGame-champion">
                    Champion du jour : <strong>{champion.display_name}</strong> — {champion.score} pts
                </div>
            )}

            <div className="HuntArena">
                <HuntCanvas
                    variant={phase === 'idle' ? 'preview' : 'game'}
                    playing={phase === 'playing'}
                    sessionKey={sessionKey}
                    reducedMotion={reducedMotion}
                    onSnapshot={phase === 'playing' ? handleSnapshot : undefined}
                    onFinished={handleFinished}
                />

                {phase === 'playing' && snapshot && (
                    <div className="HuntArena-hud">
                        <HuntHUD snapshot={snapshot} />
                    </div>
                )}

                {phase === 'idle' && (
                    <div className="HuntArena-overlay HuntArena-overlay--intro">
                        <p className="HuntArena-tagline">Touchez les oiseaux qui passent</p>
                        <ul className="HuntArena-rules">
                            <li>Oiseau <strong>+{BIRD_POINTS.normal}</strong></li>
                            <li>Rapide <strong>+{BIRD_POINTS.fast}</strong></li>
                            <li>Corbac <strong>+{BIRD_POINTS.bonus}</strong></li>
                        </ul>
                        <Button variant="success" size="large" onClick={startGame}>
                            Lancer la chasse
                        </Button>
                    </div>
                )}

                {phase === 'gameover' && finalSnapshot && (
                    <div className="HuntArena-overlay HuntArena-overlay--result">
                        <div className="HuntResult-card">
                            <p className="HuntResult-score">
                                <span className="HuntResult-scoreValue">{finalSnapshot.score}</span>
                                <span className="HuntResult-scoreUnit">pts</span>
                            </p>
                            {beatPersonalBest ? (
                                <p className="HuntResult-record">Record battu !</p>
                            ) : (
                                <p className="HuntResult-record HuntResult-record--muted">
                                    Record : {localBest} pts
                                </p>
                            )}
                            <p className="HuntResult-stats">{renderHitStats(finalSnapshot)}</p>
                            <Button variant="success" size="medium" fullWidth loading={submitting} onClick={startGame}>
                                Rejouer
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            <div className="HuntGame-footer">
                <div className="HuntGame-personalBest">
                    <span className="HuntGame-personalBest-label">Record perso</span>
                    <strong className="HuntGame-personalBest-value">{localBest}</strong>
                </div>

                {HUNT_ONLINE_FEATURES && (
                    <HuntLeaderboard
                        day={leaderboardDay}
                        week={leaderboardWeek}
                        loading={leaderboardLoading}
                        currentUserId={currentUser?.id ?? null}
                    />
                )}
            </div>
        </div>
    );
};

export default HuntGame;
